// Utility to normalize image URLs and provide resilient fallbacks for avatars/covers

// Generic default (single-letter) to avoid brand-specific initials when no user context is available
export const DEFAULT_AVATAR =
  'https://ui-avatars.com/api/?name=U&background=0D8ABC&color=fff&size=256';

// Derive initials from a name/username, up to 2 letters, uppercase
export function getInitials(name?: string | null, username?: string | null, max: number = 2): string {
  const n = (name || '').trim();
  if (n) {
    // Split by whitespace and punctuation, take first letters of first two tokens
    const tokens = n
      .replace(/[_\-|]+/g, ' ')
      .split(/\s+/)
      .filter(Boolean);
    const letters = tokens.slice(0, max).map(t => t[0] || '').join('');
    if (letters) return letters.toUpperCase();
  }
  const u = (username || '').trim();
  if (u) return u.slice(0, max).toUpperCase();
  return 'U';
}

// Build a consistent avatar placeholder URL based on user initials
export function avatarFallbackUrl(name?: string | null, username?: string | null, opts?: { bg?: string; color?: string; size?: number }): string {
  const initials = getInitials(name || undefined, username || undefined, 2);
  const bg = (opts?.bg || '0D8ABC');
  const color = (opts?.color || 'fff');
  const size = (opts?.size || 256);
  // Use ui-avatars to render initials consistently
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${bg}&color=${color}&size=${size}`;
}

// Generic logo placeholder for partners and brands
export const DEFAULT_PARTNER_LOGO =
  'https://dummyimage.com/240x120/edf2f7/94a3b8.png&text=Partner+Logo';

export function resolveImageSrc(src?: string | null): string {
  const s0 = (src || '').trim();
  if (!s0) return DEFAULT_AVATAR;
  // If already absolute (http/https), return as-is
  if (/^https?:\/\//i.test(s0)) return s0;
  // If it looks like a data URL or blob, return as-is
  if (/^(data:|blob:)/i.test(s0)) return s0;
  // Normalize a single leading slash for relative forms like 'uploads/..' or './uploads/..'
  const s = s0.replace(/^\.?\/*/, '/');
  // Route uploads via /api/uploads to ensure it hits the Node server in all deployments
  if (s.startsWith('/api/uploads/')) return s; // already API-prefixed
  if (s.startsWith('/uploads/')) return `/api${s}`;
  // If string contains '/uploads/' later in the path (rare for relative), extract from there
  const idx = s.indexOf('/uploads/');
  if (idx >= 0) return `/api${s.slice(idx)}`;
  // Default: return normalized path
  return s;
}
